/**
 * workflow_status_modal_controller.js — modal-internal lifecycle for the
 * "Set Workflow Status" modal (Stimulus).
 *
 * Element-scoped to #customStatusModal. Replaces the old initCustomStatus() init
 * in workflow_status_modal.js, which bound the footer buttons with
 * getElementById(...).addEventListener inside a TurboInit handler that only ran on
 * the *next* turbo:load. Because workflow_status_modal.js is lazy-loaded by
 * PageLoader AFTER turbo:load has already fired, that handler never ran on the
 * first visit to an entity page, leaving "Save Note" and "Add Status" dead until a
 * Turbo navigation re-fired the queue. connect() fires whenever the modal element
 * enters the DOM, independent of script load order, which removes the race.
 *
 * The modal's open/load/save behaviour stays as document-global functions in
 * workflow_status_modal.js: status_badge.html opens the modal via inline
 * onclick -> openCustomStatusModal, and this controller delegates the footer
 * clicks to handleSaveWorkflowStatus / handleAddWorkflowStatus. Those globals are
 * defined when the user can reach them (the modal can only open once
 * workflow_status_modal.js has loaded), but the typeof guards keep connect() safe
 * if a click somehow lands first.
 */

import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
        connect() {
            // Force-remove stuck Bootstrap backdrops when the modal closes
            // (Chrome mobile leaves them behind). Saved so disconnect can detach.
            this._onHidden = function () {
                if (typeof window.cleanupModalBackdrop === 'function') window.cleanupModalBackdrop();
            };
            this.element.addEventListener('hidden.bs.modal', this._onHidden);

            // The "Add Status" submit button lives in the sibling #addStatusModal,
            // outside this controller's subtree, so it can't use a data-action here.
            // Wire it directly. This controller only exists on list/detail/dashboard
            // pages, never the settings page (which has its own handler in
            // workflow_categories.js), so no settings-page guard is needed.
            this._submitBtn = document.getElementById('submitAddStatusBtn');
            if (this._submitBtn) {
                this._onSubmitAddStatus = function () {
                    if (typeof window.submitAddStatusFromDashboard === 'function') window.submitAddStatusFromDashboard();
                };
                this._submitBtn.addEventListener('click', this._onSubmitAddStatus);
            }
        }

        disconnect() {
            this.element.removeEventListener('hidden.bs.modal', this._onHidden);
            if (this._submitBtn && this._onSubmitAddStatus) {
                this._submitBtn.removeEventListener('click', this._onSubmitAddStatus);
            }
        }

        // click->workflow-status-modal#saveNote
        saveNote() {
            if (typeof window.handleSaveWorkflowStatus === 'function') window.handleSaveWorkflowStatus();
        }

        // click->workflow-status-modal#addStatus
        addStatus() {
            if (typeof window.handleAddWorkflowStatus === 'function') window.handleAddWorkflowStatus();
        }
    }
